import { inject, Injectable } from '@angular/core';
import { confirmSignIn, decodeJWT, fetchAuthSession, signIn, signOut, SignInOutput } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

import {
  authorizationRedirectUri,
  AuthFailureCause,
  ChallengeKind,
  UnsupportedStep,
  VaultError,
  VaultTokenStorage,
} from '@ninety/mobile/platform/auth';
import { environment } from '@ninety/mobile/platform/environments';

import { AuthFailureError } from './auth-failure';

/**
 * How far a Grant got at the Authorization Server.
 *
 * Both answers are successes — one finished, one needs a code — so a Grant step is not modelled as
 * an optional ID Token. The two lead to different events and neither is a failure.
 */
export type GrantStep =
  | { readonly kind: 'idToken'; readonly idToken: string }
  | { readonly kind: 'challenge'; readonly challenge: ChallengeKind };

/**
 * The Cognito steps this app can answer, and nothing else.
 *
 * `ChallengeKind` covers what a native form can collect, which is a code and nothing else. Every other
 * step Cognito can return — MFA selection, a forced password change, an unconfirmed sign-up — needs a
 * screen that does not exist, so it is not silently treated as one of these. The email *setup* step is
 * one of those: answering an email code is not enrolling the factor, and enrolment lives in web
 * settings.
 */
const CHALLENGE_STEPS: Readonly<Record<string, ChallengeKind>> = {
  CONFIRM_SIGN_IN_WITH_SMS_CODE: 'smsMfa',
  CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'totpMfa',
  CONFIRM_SIGN_IN_WITH_EMAIL_CODE: 'emailMfa',
};

/**
 * Which kind of missing screen a step Cognito asked for would need.
 *
 * {@link UnsupportedStep} is what leaves this file. Grouped rather than passed through because a
 * dashboard wants to know which screen is missing, not which literal Cognito emitted, and because
 * the literals are Amplify's to rename.
 *
 * Deliberately a lookup with a fallback rather than an exhaustive switch: Amplify has added three
 * members to this union in the releases this app has been through, and a step nobody has heard of is
 * by definition another one we cannot collect.
 */
const UNSUPPORTED_STEPS: Readonly<Record<string, UnsupportedStep>> = {
  CONFIRM_SIGN_UP: 'confirmSignUp',
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'newPasswordRequired',
  CONTINUE_SIGN_IN_WITH_MFA_SELECTION: 'mfaSelection',
  CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION: 'mfaSelection',
  CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION: 'mfaSelection',
  CONTINUE_SIGN_IN_WITH_TOTP_SETUP: 'mfaSetup',
  CONTINUE_SIGN_IN_WITH_EMAIL_SETUP: 'mfaSetup',
};

function unsupportedStep(step: string): UnsupportedStep {
  return UNSUPPORTED_STEPS[step] ?? 'other';
}

/**
 * The Authorization Server, as far as the auth machine is concerned.
 *
 * Three things happen here: a password becomes a Cognito session over SRP, a challenge code
 * completes one, and the Durable Root turns into a fresh ID Token. All three end in the same
 * currency — an ID Token, which is the only Cognito token the Resource Server will accept.
 *
 * It never sees the Cognito refresh token. Amplify holds that, and reads it through
 * `VaultTokenStorage` — wired up once at bootstrap by `provideAuth()`, so by the time anything
 * here runs, Amplify is already reading out of the Vault-backed memory that Session Restore primed.
 *
 * Nothing here configures Amplify, deliberately: `Amplify.configure()` resets the token provider's
 * storage unless it is called exactly the way `provideAuth()` calls it, so a second call from here
 * would quietly disconnect the Vault.
 *
 * ## Amplify's vocabulary stops here
 *
 * Every method either answers with a value the machine understands or raises an `AuthFailureError`
 * carrying a cause it can route on. Matching on `error.name` happens in this file, once, at the
 * boundary, and nowhere in the machine — and only for names that mean something different from each
 * other. An unrecognised error is left unclassified on purpose.
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationServer {
  private readonly storage = inject(VaultTokenStorage);

  /**
   * Authenticate a password without transmitting it.
   *
   * `USER_SRP_AUTH` is named here rather than left to Amplify's default because ADR 0002 turns on
   * it: whether the password leaves the device must not depend on a library default that a future
   * release could change.
   */
  async signInWithPassword(email: string, password: string): Promise<GrantStep> {
    let output: SignInOutput;
    try {
      output = await this.attemptSignIn(email, password);
    } catch (error) {
      throw new AuthFailureError(vaultFailure(error) ?? signInCause(error));
    }
    return this.stepFrom(output);
  }

  /** Answer an MFA Challenge, finishing the Grant or drawing another one. */
  async confirmChallenge(code: string): Promise<GrantStep> {
    let output: SignInOutput;
    try {
      output = await confirmSignIn({ challengeResponse: code });
    } catch (error) {
      // Unrecognised, so it stays unclassified — see `auth-failure.ts`. The machine leaves the
      // person on the Challenge, where retyping the code issues the Command again.
      const cause = vaultFailure(error) ?? challengeCause(error);
      throw cause ? new AuthFailureError(cause) : error;
    }
    return this.stepFrom(output);
  }

  /**
   * Tear down the Cognito session.
   *
   * Not a harmless cleanup: this removes Amplify's refresh token through the Vault-backed storage
   * and revokes it at the Authorization Server, so it destroys the Durable Root. `transition()`
   * issues it only where a Cognito session actually exists and must not survive.
   */
  async signOut(): Promise<void> {
    await signOut();
  }

  /**
   * Sign in, making room first if Amplify is still holding a session.
   *
   * That case is reachable and has nothing to do with the password: a cold start that primed the
   * Vault and then failed its restore offline leaves Amplify holding a session while the person
   * looks at the sign-in form. `signIn` asserts nobody is authenticated *before* it touches
   * Cognito, so without this a correct password fails for a reason of our own making.
   *
   * Room is made by forgetting Amplify's in-memory view, never by signing out. `signOut()` would
   * remove the Durable Root from the Vault and revoke it, and it would do so before knowing whether
   * the password was right — so the offline device this exists for would lose the session it was
   * about to restore. Forgetting costs nothing if the attempt then fails.
   */
  private async attemptSignIn(email: string, password: string): Promise<SignInOutput> {
    try {
      return await this.srpSignIn(email, password);
    } catch (error) {
      // The one failure here worth a second attempt. Everything else, including the retry's own
      // failure, is left to the caller to classify.
      if (errorName(error) !== 'UserAlreadyAuthenticatedException') throw error;
      this.storage.forget();
      return this.srpSignIn(email, password);
    }
  }

  private srpSignIn(email: string, password: string): Promise<SignInOutput> {
    return signIn({ username: email, password, options: { authFlowType: 'USER_SRP_AUTH' } });
  }

  /**
   * Turn Amplify's next step into the answer the machine is waiting for.
   *
   * A step that is neither a code we can collect nor a finished Grant is a real scenario — an
   * administrator-created account is asked to change its password at first sign-in, and MFA
   * selection fires for anyone with both SMS and TOTP enabled — but each needs a screen this app
   * does not have.
   *
   * It is `unsupportedChallenge` rather than `invalidCredentials`, because the password was right
   * and saying otherwise sends someone to change one that works. A bare `throw` escapes
   * `classifyFailure` unclassified and leaves the Grant behind a spinner, so the cause exists to say
   * "your account needs something we cannot ask for".
   */
  private async stepFrom(output: SignInOutput): Promise<GrantStep> {
    const step = output.nextStep.signInStep;
    const challenge = CHALLENGE_STEPS[step];
    if (challenge) {
      return { kind: 'challenge', challenge };
    }
    if (step !== 'DONE') {
      throw new AuthFailureError(
        { kind: 'unsupportedChallenge', detail: unsupportedStep(step) },
        `the Authorization Server asked for ${step}, which this app cannot answer`
      );
    }
    return { kind: 'idToken', idToken: await this.grantedIdToken() };
  }

  /**
   * The ID Token a just-finished Grant produced.
   *
   * No `forceRefresh`, unlike `refreshIdToken`: these tokens were minted moments ago, so forcing a
   * refresh would spend a round trip replacing them with equivalents. An empty answer here is not a
   * spent Durable Root — Amplify said the Grant was done — so it is left unclassified as the bug it
   * is, rather than reported as a refusal.
   */
  private async grantedIdToken(): Promise<string> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('the Authorization Server completed a Grant without an ID Token');
    }
    return idToken;
  }

  /**
   * Redeem an authorization code for a session, and answer with its ID Token.
   *
   * The one path here that is not an Amplify call, because Amplify offers no way to redeem a code it
   * did not itself request. `signInWithRedirect` owns the whole browser leg — it generates its own
   * PKCE material into `localStorage` and, on the web build Capacitor runs, completes by reading
   * `window.location.href` after navigating the WebView away from the app. Neither half survives a
   * custom-scheme callback. So this leg is ours: the request in `authorizationUrl`, the redemption
   * here, and the `state` check in `transition()` where the nonce it is compared against lives.
   *
   * ## Two things this must not skip
   *
   * **`redirect_uri` is sent again.** RFC 6749 §4.1.3 requires it to match the authorization
   * request, and Cognito answers `invalid_grant` when it does not — which looks exactly like a spent
   * code. One constant, used by both legs.
   *
   * **The token set goes into Amplify's store.** The app cannot hold the Durable Root, so a redeemed
   * code that is not handed over is a session that works until the app is closed.
   */
  async redeemAuthorizationCode(code: string, codeVerifier: string): Promise<string> {
    const granted = await this.postAuthorizationCode(code, codeVerifier);
    await this.cacheGrantedTokens(granted);
    return granted.idToken;
  }

  /**
   * POST the code to the token endpoint, or fail with a cause that says how.
   *
   * `fetch` rather than `HttpClient`: this is not a Ninety API call, so it must not pass through the
   * auth interceptor — which would try to attach an Access Token to the request that exists to
   * obtain one, and could trigger a Session Restore while a Grant is in flight. It is also what
   * Amplify itself uses for this endpoint, so CapacitorHttp's native patch covers it identically.
   */
  private async postAuthorizationCode(code: string, codeVerifier: string): Promise<GrantedTokens> {
    // Form-encoded, per RFC 6749 §4.1.3, and via `URLSearchParams` so every value is escaped. The
    // verifier is the one value in the app that must survive transport unaltered — a `+` read as a
    // space produces an `invalid_grant` that looks like an intercepted code.
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: environment.userPoolWebClientId,
      redirect_uri: authorizationRedirectUri(),
      code,
      code_verifier: codeVerifier,
    });

    let response: Response;
    try {
      response = await fetch(`https://${environment.cognitoDomain}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // The only way `fetch` rejects is a request that never got an answer.
      throw new AuthFailureError({ kind: 'network' }, 'the token endpoint could not be reached');
    }

    if (!response.ok) {
      // The response body carries an OAuth `error` code, and it is deliberately not read: it would
      // have to be summarised to be useful and there is nothing in it the machine routes on. Which
      // request failed is already in `auth.transition`.
      throw new AuthFailureError(tokenEndpointCause(response.status), 'the token endpoint refused the code');
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      // A 200 whose body is not JSON — a proxy error page, most likely. `grantedTokens` refuses it
      // as the incomplete answer it is, rather than letting a parse error escape and leave the Grant
      // sitting in `requestingTokens` with nothing to end it.
      payload = null;
    }

    return grantedTokens(payload);
  }

  /**
   * Hand the token set to Amplify, which persists it through the Vault-backed storage.
   *
   * `setTokens` is the same call Amplify makes after its own sign-ins — it writes through the
   * `KeyValueStorage` that `provideAuth()` installed, so the six durable keys reach the Keychain and
   * the next launch restores from them. Nothing else here writes a credential.
   *
   * **`setOAuthMetadata` is deliberately not called.** Amplify's `signOut()` reads it, and a `true`
   * there routes sign-out through `oAuthSignOutRedirect` — which navigates the WebView to Cognito's
   * logout endpoint. `cognitoSignOut` is issued from several places in the machine, none of which
   * expect the app to be navigated away, and the flag is not one of the durable keys so it would
   * only do it for the rest of that one launch.
   */
  private async cacheGrantedTokens(granted: GrantedTokens): Promise<void> {
    const accessToken = decodeJWT(granted.accessToken);
    const issuedAtMs = Number(accessToken.payload.iat ?? 0) * 1_000;

    try {
      await cognitoUserPoolsTokenProvider.tokenOrchestrator.setTokens({
        tokens: {
          accessToken,
          idToken: decodeJWT(granted.idToken),
          refreshToken: granted.refreshToken,
          username: granted.username,
          // Amplify checks expiry against the issuer's clock rather than this device's, the same way
          // it does after any other sign-in. Zero when the token carries no `iat` to measure from.
          clockDrift: issuedAtMs > 0 ? issuedAtMs - Date.now() : 0,
        },
      });
    } catch (error) {
      // The Vault write happens inside this call, so an OS-level storage failure arrives here. It is
      // not a refused code, and reporting it as one would end a Grant that succeeded.
      const cause = vaultFailure(error);
      if (cause) throw new AuthFailureError(cause);
      throw error;
    }
  }

  /**
   * Force the Authorization Server to mint a new ID Token from the Durable Root.
   *
   * `forceRefresh` is load-bearing. Amplify caches the ID Token for its full one-day lifetime and
   * returns the cached copy otherwise, so a resume after that lifetime would hand back the same
   * expired token and the restore would achieve nothing.
   */
  async refreshIdToken(): Promise<string> {
    let session: Awaited<ReturnType<typeof fetchAuthSession>>;
    try {
      session = await fetchAuthSession({ forceRefresh: true });
    } catch (error) {
      throw new AuthFailureError(vaultFailure(error) ?? refreshCause(error));
    }

    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      // Amplify reports "no session" by returning empty rather than by throwing. We only get here
      // having already found a Durable Root in the Vault, so an empty answer means it is spent.
      //
      // A different `detail` from the throwing path above: the two share a cause, so `detail` is
      // the only thing that separates a refresh token Cognito bound to a device we cannot prove any
      // more from one that simply expired.
      throw new AuthFailureError(
        { kind: 'refreshRejected', detail: 'noIdToken' },
        'the Authorization Server returned no ID Token'
      );
    }
    return idToken;
  }
}

/**
 * The values a redeemed code has to produce before it counts as a session.
 *
 * A narrowed shape rather than the endpoint's response, so nothing downstream has to re-check for
 * the pieces — and so the check happens exactly once, in {@link grantedTokens}.
 */
interface GrantedTokens {
  readonly accessToken: string;
  readonly idToken: string;
  /** The Durable Root. Never held anywhere but Amplify's store. */
  readonly refreshToken: string;
  /** Amplify reconstructs every storage key name from this, so an absent one is a lost session. */
  readonly username: string;
}

/**
 * Read a token response, or refuse it.
 *
 * A 200 that is missing any of these is a misconfiguration rather than a refusal. Continuing with
 * whatever arrived is how a sign-in succeeds and then cannot be restored. The `username` claim is
 * included because Amplify derives `LastAuthUser` and every other key name from it — Amplify's own
 * OAuth path falls back to the literal string `'username'` when it is absent, which stores a session
 * under a name nothing will look for.
 */
function grantedTokens(response: unknown): GrantedTokens {
  const body = (response ?? {}) as Record<string, unknown>;
  const accessToken = typeof body['access_token'] === 'string' ? body['access_token'] : null;
  const idToken = typeof body['id_token'] === 'string' ? body['id_token'] : null;
  const refreshToken = typeof body['refresh_token'] === 'string' ? body['refresh_token'] : null;
  const username = accessToken === null ? null : usernameFrom(accessToken);

  if (accessToken === null || idToken === null || refreshToken === null || username === null) {
    throw new AuthFailureError(
      { kind: 'authorizationFailed', detail: 'incompleteTokens' },
      'the token endpoint answered without a whole session'
    );
  }

  return { accessToken, idToken, refreshToken, username };
}

/** The Cognito username an access token was minted for — `Google_1234…` for a federated identity. */
function usernameFrom(accessToken: string): string | null {
  const username = decodeJWT(accessToken).payload['username'];
  return typeof username === 'string' && username.length > 0 ? username : null;
}

/**
 * What a token endpoint status means.
 *
 * Split the same way {@link httpCause} splits the Resource Server's, and for the same reason: only a
 * 4xx is Cognito refusing this code. A 5xx or a 429 is Cognito having trouble, and labelling that as
 * a refusal would put "your sign-in was rejected" on a screen after an outage.
 */
function tokenEndpointCause(status: number): AuthFailureCause {
  return status >= 500 || status === 429
    ? { kind: 'network' }
    : { kind: 'authorizationFailed', detail: 'codeRejected' };
}

/**
 * Whether an error out of Amplify is the network rather than a refusal.
 *
 * Matching on error shapes belongs at this boundary and nowhere in the machine, because someone has
 * to translate Amplify's vocabulary — and the bias is safe: anything unrecognised is treated as a
 * refusal only after this returns false, and a refusal is the one answer that ends a session.
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const amplifyError = error as { name?: string; underlyingError?: unknown };
  return amplifyError?.name === 'NetworkError' || amplifyError?.underlyingError instanceof TypeError;
}

function errorName(error: unknown): string {
  return (error as { name?: string })?.name ?? '';
}

/**
 * Whether the Authorization Server declined to answer rather than refusing what was asked.
 *
 * `TooManyRequestsException` is the name Cognito documents on both `InitiateAuth` and
 * `RespondToAuthChallenge`. `LimitExceededException` is the attempt-limit refusal — "Attempt limit
 * exceeded, please try after some time" — which the API reference lists for neither call but which the
 * pool raises in practice, so it is matched here rather than left to collapse into a wrong password.
 */
function isThrottled(error: unknown): boolean {
  const name = errorName(error);
  return name === 'TooManyRequestsException' || name === 'LimitExceededException';
}

/**
 * The classification the Vault already made, or `null` if this error did not come from it.
 *
 * Checked before every classifier below, because Amplify persists its token set from *inside*
 * `signIn`, `confirmSignIn` and a refresh — through `VaultTokenStorage` — so a Vault failure
 * surfaces from the same call as a refused credential. It arrives unwrapped: `assertServiceError`
 * only converts errors named `Error` or `TypeError`, and `TokenOrchestrator.handleErrors` rethrows
 * anything it does not recognise as an authentication error, so a `VaultError` passes through both.
 *
 * Without this the classifiers answer a question they were never asked. Reading one as a refused
 * password told someone their correct password was wrong; reading one as a refused refresh was
 * worse, because `refreshRejected` clears the Vault — so a declined biometric prompt during a
 * refresh destroyed the Durable Root it was protecting.
 */
function vaultFailure(error: unknown): AuthFailureCause | null {
  return error instanceof VaultError ? error.failure : null;
}

/**
 * What a refused password means.
 *
 * Every other non-network refusal collapses to `invalidCredentials`, because from the sign-in form
 * there is one thing to do about all of them: check what was typed and try again. The cause is safe to
 * be coarse about — it neither ends a session nor touches the Vault. That safety is why the collapse is
 * acceptable here, and it only holds for errors the Authorization Server raised: see
 * {@link vaultFailure}.
 *
 * A throttle is the one refusal worth naming, because it is the case where the collapse gives exactly
 * the wrong advice: checking what you typed and trying again is what extends a rate limit.
 */
function signInCause(error: unknown): AuthFailureCause {
  if (isNetworkError(error)) return { kind: 'network' };
  return isThrottled(error) ? { kind: 'rateLimited' } : { kind: 'invalidCredentials' };
}

/**
 * What a refused refresh means.
 *
 * The most consequential classification in this file: `refreshRejected` ends the session *and*
 * clears the Vault, so anything reaching it by guesswork costs someone their Durable Root. Only
 * errors the Authorization Server itself raised may arrive here.
 */
function refreshCause(error: unknown): AuthFailureCause {
  return isNetworkError(error)
    ? { kind: 'network' }
    : { kind: 'refreshRejected', detail: 'authorizationServerRefused' };
}

/**
 * What a refused challenge code means.
 *
 * `challengeFailed` keeps the person on the challenge to retype the code; `challengeExpired` ends
 * the Grant. Cognito raises `NotAuthorizedException` when its auth-flow session is gone, whether it
 * lapsed or was spent by too many wrong codes, and reporting that as a wrong code would leave
 * someone typing into a flow that no longer exists. `rateLimited` also keeps them there; without it a
 * throttle falls through unclassified and reaches the person as the machine's nothing-went-right copy,
 * under an instruction to try again immediately.
 */
function challengeCause(error: unknown): AuthFailureCause | null {
  if (isNetworkError(error)) return { kind: 'network' };
  if (isThrottled(error)) return { kind: 'rateLimited' };
  switch (errorName(error)) {
    case 'CodeMismatchException':
      return { kind: 'challengeFailed' };
    case 'ExpiredCodeException':
    case 'NotAuthorizedException':
      return { kind: 'challengeExpired' };
    default:
      return null;
  }
}
